import { useRpQuery } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import type { ActivateContractInput } from '~/resources/graphql/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useSetCartData } from '~/composables/use-cart'
import { useRouteLink } from '~/composables/routing'
import { useStorefrontPrefix } from '~/composables/use-storefront-prefix'
import { usePurchaseAnalytics } from '~/composables/analytics/purchase-event'

// Activate a contract with zero dollar pricing
export function useActivateContractMutation() {
    const activeFacility = useActiveFacility()
    const rpQuery = useRpQuery()
    const setCartData = useSetCartData()
    const { storefrontHomeLink } = useRouteLink()
    const storefrontPrefix = useStorefrontPrefix()
    const { emitPurchaseEventFromInvoice } = usePurchaseAnalytics()

    return useMutation({
        mutationFn: async (mutationData: ActivateContractInput) => {
            const response = await rpQuery(
                graphql(/** GraphQL */ `
                    mutation ActivateContract(
                        $input: ActivateContractInput!
                        $facilityId: ID!
                    ) {
                        activateContract(input: $input) {
                            __typename
                            ... on ActivateContractResult {
                                recordId
                                record {
                                    ... on PlanContract {
                                        referenceNumber
                                        planType
                                        customer {
                                            firstName
                                        }
                                    }
                                }
                                invoice {
                                    ...AnalyticsPurchaseInvoiceFragment
                                }
                                query {
                                    viewer {
                                        cart(facilityId: $facilityId) {
                                            ...ViewerCartFragment
                                        }
                                    }
                                }
                            }
                            ... on ContractIsActive {
                                recordId
                                record {
                                    ... on PlanContract {
                                        referenceNumber
                                        planType
                                        customer {
                                            firstName
                                        }
                                    }
                                }
                                query {
                                    viewer {
                                        cart(facilityId: $facilityId) {
                                            ...ViewerCartFragment
                                        }
                                    }
                                }
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    path
                                    message
                                }
                            }
                        }
                    }
                `),
                {
                    input: mutationData,
                    facilityId: activeFacility.value.id
                }
            )

            if (response.activateContract.__typename === 'ValidationError') {
                return response.activateContract
            }

            if (response.activateContract.__typename === 'ContractIsActive') {
                location.href = storefrontHomeLink(storefrontPrefix.value)
                return response.activateContract
            }

            if (
                response.activateContract.__typename ===
                'ActivateContractResult'
            ) {
                if (response.activateContract.query.viewer !== null) {
                    setCartData(response.activateContract.query.viewer.cart)
                }

                emitPurchaseEventFromInvoice(response.activateContract.invoice)

                return response.activateContract
            } else {
                throw new Error()
            }
        }
    })
}
